import React, { useEffect, useRef } from 'react';
import { reverseGeocode } from '../lib/geocode';

const Map = ({ coordinates, onCoordinatesChange, address, onAddressChange }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  // Initialize map
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Load Leaflet CSS and JS dynamically
    const loadLeaflet = async () => {
      if (window.L) return;

      // Load CSS
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);

      // Load JS
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      
      return new Promise((resolve) => {
        script.onload = resolve;
        document.head.appendChild(script);
      });
    };

    const initMap = async () => {
      await loadLeaflet();

      if (!mapInstanceRef.current && mapRef.current) {
        // Default to Italy center
        const defaultLat = 41.9028;
        const defaultLng = 12.4964;
        const defaultZoom = 6;

        mapInstanceRef.current = window.L.map(mapRef.current).setView(
          [defaultLat, defaultLng], 
          defaultZoom
        );

        // Add tile layer
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19,
        }).addTo(mapInstanceRef.current);

        // Add click handler for map
        mapInstanceRef.current.on('click', async (e) => {
          const { lat, lng } = e.latlng;
          onCoordinatesChange({ lat, lng });

          // Update marker
          updateMarker(lat, lng);

          // Reverse geocode to get address
          try {
            const addressResult = await reverseGeocode(lat, lng);
            if (addressResult) {
              onAddressChange(addressResult);
            }
          } catch (error) {
            console.error('Reverse geocoding error:', error);
          }
        });
      }
    };

    initMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update marker function
  const updateMarker = (lat, lng) => {
    if (!mapInstanceRef.current || !window.L) return;

    // Remove existing marker
    if (markerRef.current) {
      mapInstanceRef.current.removeLayer(markerRef.current);
    }

    // Add new marker
    markerRef.current = window.L.marker([lat, lng], {
      draggable: true
    }).addTo(mapInstanceRef.current);

    // Handle marker drag
    markerRef.current.on('dragend', async (e) => {
      const { lat, lng } = e.target.getLatLng();
      onCoordinatesChange({ lat, lng });

      try {
        const addressResult = await reverseGeocode(lat, lng);
        if (addressResult) {
          onAddressChange(addressResult);
        }
      } catch (error) {
        console.error('Reverse geocoding error:', error);
      }
    });
  };

  // Update map when coordinates change
  useEffect(() => {
    if (coordinates && mapInstanceRef.current) {
      const { lat, lng } = coordinates;
      
      // Center map on coordinates
      mapInstanceRef.current.setView([lat, lng], 15);
      
      // Update marker
      updateMarker(lat, lng);
    }
  }, [coordinates]);

  return (
    <div className="w-full h-full relative">
      <div 
        ref={mapRef} 
        className="w-full h-full rounded-lg"
        style={{ minHeight: '250px' }}
      />
      
      {!coordinates && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
          <div className="text-center text-gray-500">
            <p className="text-sm font-medium mb-2">Inserisci un indirizzo</p>
            <p className="text-xs">La mappa si aggiornerà automaticamente</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Map;